(() => {
  document.documentElement.classList.remove('no-js');

  const roots = document.querySelectorAll('[data-blender-pdp]');

  roots.forEach((root) => {
    const shopRoot = window.Shopify?.routes?.root || '/';
    const variantId = Number(root.dataset.variantId);
    const unitPrice = Number(root.dataset.unitPrice || 0);
    const currencyCode = root.dataset.currencyCode || 'USD';
    const productTitle = root.dataset.productTitle || 'Portable Blender';
    const form = root.querySelector('[data-pdp-product-form]');
    const quantityInput = root.querySelector('[data-quantity-input]');
    const quantityOutput = root.querySelector('[data-quantity]');
    const colorInput = root.querySelector('[data-color-input]');
    const colorButtons = [...root.querySelectorAll('[data-color]')];
    const linePrice = root.querySelector('[data-line-price]');
    const mainImage = root.querySelector('[data-main-image]');
    const thumbnails = [...root.querySelectorAll('.thumbnail')];
    const cartDrawer = root.querySelector('[data-cart-drawer]');
    const cartBackdrop = root.querySelector('[data-cart-backdrop]');
    const cartLines = root.querySelector('[data-cart-lines]');
    const cartEmpty = root.querySelector('[data-cart-empty]');
    const cartCount = root.querySelector('[data-cart-count]');
    const cartSubtotal = root.querySelector('[data-cart-subtotal]');
    const checkoutButton = root.querySelector('[data-checkout]');
    const purchaseStatus = root.querySelector('[data-purchase-status]');
    const checkoutStatus = root.querySelector('[data-checkout-status]');
    const toast = root.querySelector('[data-toast]');
    let quantity = 1;
    let color = 'white';
    let toastTimer;
    let lastFocusedElement = null;
    let cartRequestInFlight = false;

    const money = (cents) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: currencyCode
        }).format(Number(cents || 0) / 100);
      } catch (_) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    };

    const showToast = (message) => {
      if (!toast) return;
      toast.textContent = message;
      toast.hidden = false;
      window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(() => { toast.hidden = true; }, 1800);
    };

    const setPurchaseBusy = (busy) => {
      root.querySelectorAll('[data-add-to-cart], [data-buy-now]').forEach((button) => {
        if (button.dataset.initiallyDisabled === undefined) {
          button.dataset.initiallyDisabled = String(button.disabled);
        }
        button.disabled = busy || button.dataset.initiallyDisabled === 'true';
        button.toggleAttribute('aria-busy', busy);
      });
      const label = root.querySelector('[data-add-label]');
      if (label) {
        if (label.dataset.initialLabel === undefined) label.dataset.initialLabel = label.textContent;
        label.textContent = busy ? 'Adding…' : label.dataset.initialLabel;
      }
    };

    const setCartBusy = (busy) => {
      cartRequestInFlight = busy;
      cartDrawer?.toggleAttribute('aria-busy', busy);
      cartLines?.querySelectorAll('[data-cart-line] button').forEach((button) => {
        const isDecrease = button.getAttribute('aria-label')?.startsWith('Decrease');
        const shownQuantity = Number(button.closest('[data-cart-line]')?.querySelector('.cart-line__quantity output')?.textContent || 1);
        button.disabled = busy || (isDecrease && shownQuantity <= 1);
      });
      if (checkoutButton) checkoutButton.disabled = busy || Number(cartCount?.textContent || 0) === 0;
    };

    const createCartLine = (item) => {
      const row = document.createElement('article');
      row.className = 'cart-line';
      row.dataset.cartLine = '';
      row.dataset.cartKey = item.key;

      const imageLink = document.createElement('a');
      imageLink.className = 'cart-line__image';
      imageLink.href = item.url || '#product';
      imageLink.setAttribute('aria-label', `View ${item.product_title || productTitle}`);
      const imageUrl = item.featured_image?.url || item.image;
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = item.product_title || productTitle;
        image.width = 88;
        image.height = 88;
        image.loading = 'lazy';
        imageLink.appendChild(image);
      }

      const information = document.createElement('div');
      information.className = 'cart-line__info';
      const title = document.createElement('a');
      title.className = 'cart-line__title';
      title.href = item.url || '#product';
      title.textContent = item.product_title || productTitle;

      const meta = document.createElement('span');
      meta.className = 'cart-line__meta';
      const selectedColor = item.properties?.Color;
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      meta.textContent = [selectedColor, variantTitle].filter(Boolean).join(' · ');
      if (!meta.textContent) meta.hidden = true;

      const controls = document.createElement('div');
      controls.className = 'cart-line__actions';
      controls.setAttribute('aria-label', `Quantity for ${item.product_title || productTitle}`);

      const quantityControls = document.createElement('div');
      quantityControls.className = 'cart-line__quantity';

      const decrease = document.createElement('button');
      decrease.type = 'button';
      decrease.dataset.cartQuantity = String(Math.max(1, item.quantity - 1));
      decrease.setAttribute('aria-label', `Decrease ${item.product_title || productTitle} quantity`);
      decrease.textContent = '−';
      decrease.disabled = item.quantity <= 1;

      const quantityValue = document.createElement('output');
      quantityValue.textContent = String(item.quantity);
      quantityValue.setAttribute('aria-label', `Quantity ${item.quantity}`);

      const increase = document.createElement('button');
      increase.type = 'button';
      increase.dataset.cartQuantity = String(item.quantity + 1);
      increase.setAttribute('aria-label', `Increase ${item.product_title || productTitle} quantity`);
      increase.textContent = '+';

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'cart-line__remove';
      remove.dataset.cartRemove = '';
      remove.setAttribute('aria-label', `Remove ${item.product_title || productTitle} from shopping bag`);
      remove.textContent = 'Remove';

      quantityControls.append(decrease, quantityValue, increase);
      controls.append(quantityControls, remove);

      const price = document.createElement('strong');
      price.className = 'cart-line__price';
      price.textContent = money(item.final_line_price);
      information.append(title, price, meta, controls);
      row.append(imageLink, information);
      return row;
    };

    const renderCart = (cart) => {
      cartLines?.querySelectorAll('[data-cart-line]').forEach((line) => line.remove());
      const itemCount = Number(cart.item_count || 0);
      if (cartCount) cartCount.textContent = String(itemCount);
      if (cartSubtotal) cartSubtotal.textContent = money(cart.total_price || 0);
      if (cartEmpty) cartEmpty.hidden = itemCount > 0;
      if (checkoutButton) checkoutButton.disabled = itemCount === 0;

      cart.items.forEach((item) => cartLines?.appendChild(createCartLine(item)));
    };

    const requestCart = async (url, options = {}) => {
      const response = await fetch(`${shopRoot}${url}`, {
        ...options,
        headers: { Accept: 'application/json', ...options.headers }
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || 'Unable to update the shopping bag.');
      }
      return response.json();
    };

    const fetchCart = async () => {
      const cart = await requestCart('cart.js');
      renderCart(cart);
      return cart;
    };

    const openCart = async ({ refresh = true } = {}) => {
      lastFocusedElement = document.activeElement;
      cartDrawer.hidden = false;
      cartBackdrop.hidden = false;
      document.body.classList.add('locked');
      root.querySelector('[data-cart-close]')?.focus();
      if (refresh) {
        setCartBusy(true);
        try {
          await fetchCart();
        } catch (error) {
          if (checkoutStatus) checkoutStatus.textContent = error.message;
        } finally {
          setCartBusy(false);
        }
      }
    };

    const closeCart = () => {
      cartDrawer.hidden = true;
      cartBackdrop.hidden = true;
      document.body.classList.remove('locked');
      if (lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
    };

    const changeCartLine = async (key, nextQuantity, requestedControl) => {
      if (!key || cartRequestInFlight) return;
      const isRemoval = nextQuantity === 0;
      if (checkoutStatus) checkoutStatus.textContent = isRemoval ? 'Removing item…' : 'Updating quantity…';
      setCartBusy(true);
      try {
        const cart = await requestCart('cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: nextQuantity })
        });
        renderCart(cart);
        if (checkoutStatus) checkoutStatus.textContent = isRemoval ? 'Item removed.' : 'Quantity updated.';
        showToast(isRemoval ? 'Item removed' : 'Shopping bag updated');
        if (isRemoval) {
          root.querySelector('[data-cart-close]')?.focus();
        } else {
          const updatedRow = [...cartLines.querySelectorAll('[data-cart-line]')].find((line) => line.dataset.cartKey === key);
          updatedRow?.querySelector(requestedControl === 'increase' ? '[aria-label^="Increase"]' : '[aria-label^="Decrease"]')?.focus();
        }
      } catch (error) {
        if (checkoutStatus) checkoutStatus.textContent = error.message;
        showToast(error.message);
      } finally {
        setCartBusy(false);
      }
    };

    const addToCart = async ({ checkout = false } = {}) => {
      if (!variantId) return;
      setPurchaseBusy(true);
      if (purchaseStatus) purchaseStatus.textContent = 'Adding your blender…';
      try {
        await requestCart('cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity,
              properties: { Color: color.charAt(0).toUpperCase() + color.slice(1) }
            }]
          })
        });
        await fetchCart();
        if (purchaseStatus) purchaseStatus.textContent = `${quantity} item${quantity === 1 ? '' : 's'} added to your bag.`;
        showToast(`${quantity} × blender added`);
        if (checkout) {
          window.location.assign(`${shopRoot}checkout`);
          return;
        }
        await openCart({ refresh: false });
      } catch (error) {
        if (purchaseStatus) purchaseStatus.textContent = error.message;
        showToast(error.message);
      } finally {
        setPurchaseBusy(false);
      }
    };

    const showImage = (button) => {
      thumbnails.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      mainImage.src = button.hasAttribute('data-variant-image') ? button.dataset[`${color}Src`] : button.dataset.src;
      mainImage.alt = button.dataset.alt;
      mainImage.className = button.dataset.fit === 'cover' ? 'fit-cover' : 'fit-contain';
      root.querySelector('[data-image-index]').textContent = button.dataset.galleryIndex;
    };

    thumbnails.forEach((button) => button.addEventListener('click', () => showImage(button)));
    colorButtons.forEach((button) => button.addEventListener('click', () => {
      color = button.dataset.color;
      const colorLabel = color.charAt(0).toUpperCase() + color.slice(1);
      colorInput.value = colorLabel;
      root.querySelector('[data-color-label]').textContent = colorLabel;
      root.querySelector('[data-mobile-color]').textContent = colorLabel;
      colorButtons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      root.querySelectorAll('[data-variant-image]').forEach((item) => {
        item.querySelector('img').src = item.dataset[`${color}Src`];
      });
      showImage(root.querySelector('[data-variant-image]'));
    }));

    const quantityButtons = [...root.querySelectorAll('[data-quantity-action]')];
    const updateQuantity = () => {
      quantityInput.value = String(quantity);
      quantityOutput.textContent = String(quantity);
      linePrice.textContent = money(unitPrice * quantity);
      quantityButtons.forEach((button) => {
        button.disabled = button.dataset.quantityAction === 'decrease' && quantity === 1;
      });
    };

    quantityButtons.forEach((button) => button.addEventListener('click', () => {
      quantity = Math.max(1, Math.min(9, quantity + (button.dataset.quantityAction === 'increase' ? 1 : -1)));
      updateQuantity();
    }));
    updateQuantity();

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      addToCart();
    });
    root.querySelector('[data-buy-now]')?.addEventListener('click', () => addToCart({ checkout: true }));
    root.querySelector('[data-cart-open]')?.addEventListener('click', () => openCart());
    root.querySelectorAll('[data-cart-close]').forEach((button) => button.addEventListener('click', closeCart));
    cartBackdrop?.addEventListener('click', closeCart);
    cartLines?.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      const row = button?.closest('[data-cart-line]');
      if (!button || !row) return;
      if (button.hasAttribute('data-cart-remove')) {
        changeCartLine(row.dataset.cartKey, 0, 'remove');
        return;
      }
      if (button.hasAttribute('data-cart-quantity')) {
        changeCartLine(row.dataset.cartKey, Number(button.dataset.cartQuantity), button.textContent === '+' ? 'increase' : 'decrease');
      }
    });
    checkoutButton?.addEventListener('click', () => {
      if (!checkoutButton.disabled) window.location.assign(`${shopRoot}checkout`);
    });

    document.addEventListener('keydown', (event) => {
      if (cartDrawer.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCart();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...cartDrawer.querySelectorAll('a[href], button:not([disabled])')].filter((element) => !element.hidden);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    root.querySelectorAll('details').forEach((detail) => detail.addEventListener('toggle', () => {
      const marker = detail.querySelector('summary span');
      if (marker) marker.textContent = detail.open ? '−' : '+';
    }));
  });
})();
